import { FC, memo, useMemo } from 'react';

import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MaterialTooltip from '@material-ui/core/Tooltip';
import clsx from 'clsx';

import { useStyles } from './Tooltip.styles';
import { ITooltipProps } from './Tooltip.types';

/**
 * @component Tooltip
 * @description A tooltip is a layer that appears above other content on the page, displaying
 * additional information upon hover, focus, or click. Tooltips provide contextual information
 * that enhances usability without cluttering the interface.
 *
 * @designSystem
 * - **Placement Options**:
 *   - `right` (default): When space is available to the right
 *   - `left`: When elements are near the right edge
 *   - `top`: When vertical space above is available
 *   - `bottom`: When elements are near the top of the viewport
 *   - Additional variants: Add `-start` or `-end` for more precise positioning
 *
 * - **Interaction Types**:
 *   - Hover: Appears on mouse hover (set `isHover={true}`)
 *   - Click: Appears on click and stays until clicked away (set `isClick={true}`)
 *   - Interactive: Allow interaction with tooltip content (set `interactive={true}`)
 *
 * - **Visual Elements**:
 *   - Arrow: Optional directional arrow pointing to the trigger element (set `isArrow={true}`)
 *   - Content: Supports text, rich content, or interactive elements (when interactive)
 *
 * @usage
 * - Use for supplementary information that's not required for primary task completion
 * - Use for clarifying icon-only or otherwise ambiguous UI elements
 * - Keep tooltip content concise, typically 1-2 short sentences
 * - Only one tooltip should be visible at a time
 * - Don't cover the element being explained
 * - Use interactive tooltips when users need to interact with content without leaving current context
 *
 * @accessibility
 * - Ensure tooltips are accessible via keyboard focus
 * - Don't use tooltips as the only way to access critical information
 * - Support screen readers with appropriate ARIA attributes
 * - Provide sufficient delay for hover tooltips to prevent accidental triggering
 *
 * @example
 * ```jsx
 * // Basic hover tooltip
 * <Tooltip title="Additional information" isHover>
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * // Click tooltip with arrow
 * <Tooltip title="Click tooltip content" isClick isArrow>
 *   <Button>Click me</Button>
 * </Tooltip>
 *
 * // Interactive tooltip with rich content
 * <Tooltip
 *   title={<div><Typography>Rich content</Typography><Button>Click me</Button></div>}
 *   isHover
 *   interactive
 * >
 *   <Button>Interactive tooltip</Button>
 * </Tooltip>
 * ```
 */
export const Tooltip: FC<ITooltipProps> = memo(
  ({
    isClick,
    isHover,
    isArrow,
    interactive,
    open,
    children,
    placement = 'right',
    title = '',
    disabled,
    hidden,
    zIndex = 1000,
    classes,
    PopperProps: externalPopperProps,
    onClose = () => null,
    ...props
  }) => {
    const styles = useStyles({ zIndex });

    const popperProps = useMemo(
      () => ({
        ...externalPopperProps,
        popperOptions: {
          ...externalPopperProps?.popperOptions,
          positionFixed: true,
        },
      }),
      [externalPopperProps],
    );

    if (isClick) {
      return (
        <ClickAwayListener onClickAway={onClose}>
          <div className={styles.tooltipWrapper}>
            <MaterialTooltip
              {...props}
              PopperProps={popperProps}
              classes={{
                ...classes,
                popper: clsx(styles.popper, hidden && 'hidden', classes?.popper),
                tooltip: clsx(
                  styles.tooltip,
                  placement === 'right' && 'placementRight',
                  placement === 'left' && 'placementLeft',
                  placement === 'top' && 'placementTop',
                  placement === 'bottom' && 'placementBottom',
                  classes?.tooltip,
                ),
                arrow: clsx(styles.arrow, classes?.arrow),
                tooltipArrow: clsx(styles.tooltipArrow, classes?.tooltipArrow),
              }}
              title={title}
              interactive={interactive}
              arrow={isArrow}
              open={open}
              placement={placement}
              disableFocusListener={disabled || (props.disableFocusListener ?? isClick)}
              disableTouchListener={disabled || (props.disableTouchListener ?? isClick)}
              disableHoverListener={disabled || (props.disableHoverListener ?? isClick)}
              onClose={onClose}
            >
              {children}
            </MaterialTooltip>
          </div>
        </ClickAwayListener>
      );
    }

    return (
      <MaterialTooltip
        {...props}
        PopperProps={popperProps}
        classes={{
          ...classes,
          popper: clsx(styles.popper, hidden && 'hidden', classes?.popper),
          tooltip: clsx(styles.tooltip, classes?.tooltip),
          arrow: clsx(styles.arrow, classes?.arrow),
          tooltipArrow: clsx(styles.tooltipArrow, classes?.tooltipArrow),
        }}
        title={title}
        interactive={interactive}
        arrow={isArrow}
        open={open}
        placement={placement}
        disableHoverListener={disabled}
        disableFocusListener={disabled || (props.disableFocusListener ?? isHover)}
        disableTouchListener={disabled || (props.disableTouchListener ?? isHover)}
        onClose={onClose}
      >
        {children}
      </MaterialTooltip>
    );
  },
);

Tooltip.displayName = 'Tooltip';
